import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, CircleNotch, CheckCircle, Coins } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { fetchOpenInvoices, allocatePayment, type OpenInvoice } from '@/lib/paymentsApi';
import { getApiErrorMessage } from '@/lib/api';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  paymentId: string;
  payer: string | null;
  unallocated: number;
  currency: string;
  onClose: () => void;
  onAllocated: () => void;
}

export function AllocatePaymentModal({ paymentId, payer, unallocated, currency, onClose, onAllocated }: Props) {
  const qc = useQueryClient();
  const [amounts, setAmounts] = useState<Record<string, string>>({});

  const money = (n: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 2 }).format(n);

  const invoicesQ = useQuery<OpenInvoice[]>({
    queryKey: ['open-invoices-for-allocation'],
    queryFn: () => fetchOpenInvoices(),
    staleTime: 30_000,
  });

  const entered = useMemo(
    () => Object.values(amounts).reduce((s, v) => s + (parseFloat(v) || 0), 0),
    [amounts],
  );
  const remaining = Math.round((unallocated - entered) * 100) / 100;

  const allocateMut = useMutation({
    mutationFn: () => {
      const allocations = Object.entries(amounts)
        .map(([feeNoteId, v]) => ({ feeNoteId, amountApplied: parseFloat(v) || 0, isWriteOff: false }))
        .filter((a) => a.amountApplied > 0);
      return allocatePayment(paymentId, allocations);
    },
    onSuccess: () => {
      toast.success('Payment allocated');
      qc.invalidateQueries({ queryKey: ['unallocated-funds'] });
      qc.invalidateQueries({ queryKey: ['money-requiring-attention'] });
      onAllocated();
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not allocate the payment')),
  });

  const invoices = invoicesQ.data ?? [];
  const totalEntered = Math.round(entered * 100) / 100;
  const overspent = totalEntered > unallocated + 0.005;
  const canSubmit = totalEntered > 0 && !overspent && !allocateMut.isPending;

  function setAmount(inv: OpenInvoice, raw: string) {
    const bal = parseFloat(inv.balance);
    const others = entered - (parseFloat(amounts[inv.id] || '0') || 0);
    const cap = Math.min(bal, Math.max(0, unallocated - others));
    let v = raw;
    const n = parseFloat(raw);
    if (!Number.isNaN(n) && n > cap) v = cap.toFixed(2);
    setAmounts((prev) => ({ ...prev, [inv.id]: v }));
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-6"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget && !allocateMut.isPending) onClose(); }}
    >
      <ScrollLock />
      <Card className="my-auto flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight">
              <Coins size={15} weight="duotone" className="text-brand-600 dark:text-brand-400" />
              Allocate payment
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {payer ? `${payer} · ` : ''}{money(unallocated)} to allocate
            </p>
          </div>
          <button
            type="button"
            onClick={() => !allocateMut.isPending && onClose()}
            disabled={allocateMut.isPending}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {invoicesQ.isLoading && (
            <div className="flex items-center gap-2 py-6 text-xs text-muted-foreground">
              <CircleNotch size={13} className="animate-spin" /> Loading open fee notes…
            </div>
          )}

          {!invoicesQ.isLoading && invoices.length === 0 && (
            <p className="py-6 text-center text-xs text-muted-foreground">
              No open fee notes to allocate against. Raise or send a fee note first.
            </p>
          )}

          <div className="space-y-2">
            {invoices.map((inv) => (
              <div key={inv.id} className="flex items-center gap-3 rounded-md border border-border px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-foreground">{inv.case?.caseName ?? inv.feeNoteNumber}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {inv.feeNoteNumber} · {money(parseFloat(inv.balance))} outstanding
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-muted-foreground">{currency === 'GBP' ? '£' : ''}</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={amounts[inv.id] ?? ''}
                    onChange={(e) => setAmount(inv, e.target.value)}
                    disabled={allocateMut.isPending}
                    placeholder="0.00"
                    className="w-24 rounded-md border border-input bg-background px-2 py-1 text-right text-sm tabular-nums"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-border px-5 py-3">
          <div className="mb-2 flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Allocating {money(totalEntered)} of {money(unallocated)}</span>
            <span className={remaining === 0 ? 'font-medium text-emerald-700 dark:text-emerald-300' : 'text-muted-foreground'}>
              {remaining === 0 ? (
                <span className="flex items-center gap-1"><CheckCircle size={12} weight="fill" /> fully allocated</span>
              ) : (
                `${money(remaining)} left`
              )}
            </span>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={allocateMut.isPending}>
              Cancel
            </Button>
            <Button type="button" size="sm" disabled={!canSubmit} onClick={() => allocateMut.mutate()} className="gap-1.5">
              {allocateMut.isPending ? <CircleNotch size={14} className="animate-spin" /> : <Coins size={14} weight="fill" />}
              Allocate {money(totalEntered)}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
